import React from 'react';
import { ResponsiveTooltip, Typography } from '../../../../components';
import { PublicLinkItemData } from '../types';
import { calculateRevenue, calculateArpu, calculateArpd } from '../utils';
import { Tooltip } from '@heroui/react';
import { GroupUserIc, MessageIc, MoneyIc, UserCheckIc } from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';

interface StatisticsCellProps {
  item: PublicLinkItemData;
}

const StatisticsCell: React.FC<StatisticsCellProps> = ({ item }) => {
  const { t } = useTranslations();

  // Используем transactions_statistic.total если доступен, иначе вычисляем
  const revenue = item.transactions_statistic?.total
    ? parseFloat(String(item.transactions_statistic.total))
    : calculateRevenue(item.subs.price, item.subs.count);

  const arpu = calculateArpu(revenue, item.subs.count);
  const arpd = calculateArpd(revenue, item.subs.with_gte_3_chat_messages_count);

  const interpolate = (template: string, vars: Record<string, any>): string => {
    return template.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] ?? '');
  };

  const totalSubscribers = item.subs.count || 1;
  const buyersCount = item.subscribed_count || 0;
  const buyersPercent = ((buyersCount / totalSubscribers) * 100).toFixed(0);
  const chatsCount = item.subs.with_gte_3_chat_messages_count || 0;
  const chatsPercent = ((chatsCount / totalSubscribers) * 100).toFixed(0);

  // Проверяем доступность данных транзакций
  const hasTransactionStats =
    item.transactions_statistic &&
    (item.transactions_statistic.total !== undefined ||
      item.transactions_statistic.count !== undefined);
  return (
    <div className="flex flex-col gap-1 items-start justify-center w-full">
      <div className="flex flex-wrap gap-2 h-4 items-center w-full">
        {/* Subscribers */}
        {/* <div className="flex gap-1 items-center">
          <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
            <path d="M4 5a2 2 0 100-4 2 2 0 000 4zM4 6.5c-2.21 0-4 1.12-4 2.5V11h8V9c0-1.38-1.79-2.5-4-2.5z" fill="#71717A"/>
          </svg>
          <Typography variant="body3" className="text-auth-ink">
            {item.subs.count}
          </Typography>
          {item.subs.count > 0 && (
            <Typography variant="body3" className="text-auth-strength-strong">
              +{item.subs.count}
            </Typography>
          )}
        </div> */}
        <ResponsiveTooltip content={t('partner.orders.tooltips.subscribers')} placement="top">
          <div className="flex items-center gap-1">
            <GroupUserIc className="text-gray-600" />
            <Typography variant="body3" className="text-gray-900">
              {item.subs.count}
            </Typography>
          </div>
        </ResponsiveTooltip>
        {/* Active subscribers */}
        {item.subs.with_gte_3_chat_messages_count !== null && (
          <div className="flex gap-1 items-center">
            <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
              <path d="M4 5a2 2 0 100-4 2 2 0 000 4z" fill="#71717A" />
              <path d="M4 6.5c-2.21 0-4 1.12-4 2.5V11h8V9c0-1.38-1.79-2.5-4-2.5z" fill="#71717A" />
              <path d="M8 7l1 1 2-2" stroke="#71717A" strokeWidth="1.5" />
            </svg>
            <Typography variant="body3" className="text-auth-ink">
              {item.subs.with_gte_3_chat_messages_count}
            </Typography>
          </div>
        )}
        <ResponsiveTooltip
          content={interpolate(t('partner.orders.tooltips.buyersAndChats'), {
            buyersCount,
            buyersPercent,
            chatsCount,
            chatsPercent,
          })}
          placement="top"
        >
          <div className="flex items-center gap-1">
            <UserCheckIc className="text-gray-600" />
            <Typography variant="body3" className="text-gray-900">
              {item.subscribed_count || 0}
            </Typography>
          </div>
        </ResponsiveTooltip>
        {/* Messages - показываем только если есть данные */}
        {hasTransactionStats && item.transactions_statistic?.['5'] !== undefined && (
          <ResponsiveTooltip content={t('partner.orders.tooltips.messages')} placement="top">
            <div className="flex items-center gap-1">
              <MessageIc className="text-gray-600" />
              <Typography variant="body3" className="text-gray-900">
                {item.transactions_statistic['5'] || 0}
              </Typography>
            </div>
          </ResponsiveTooltip>
        )}
        {/* Purchases - показываем только если есть данные транзакций */}
        {hasTransactionStats && (
          <ResponsiveTooltip content={t('partner.orders.tooltips.purchases')} placement="top">
            <div className="flex items-center gap-1">
              <MoneyIc className="text-gray-600" />
              <Typography variant="body3" className="text-gray-900">
                {item.transactions_statistic?.count || 0}
              </Typography>
            </div>
          </ResponsiveTooltip>
        )}
      </div>

      {/* ARPU metrics - показываем только если есть данные транзакций */}
      {hasTransactionStats && (
        <div className="flex flex-wrap gap-1 items-center w-full">
          <div className="flex gap-1 items-center">
            <Typography variant="body3" className="text-zinc-500">
              ARPU:
            </Typography>
            <Typography variant="label2SemiBold" className="text-auth-ink">
              {arpu}
            </Typography>
          </div>
          <div className="flex gap-1 items-center">
            <Typography variant="body3" className="text-zinc-500">
              ARPD:
            </Typography>
            <Typography variant="label2SemiBold" className="text-auth-ink">
              {arpd}
            </Typography>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsCell;
